import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const api = process.env.REACT_APP_API_URL // Fixed: Consistent variable name with top code

// Fetch all departments
export const fetchDepartments = createAsyncThunk("department/fetchDepartments", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${api}/departments`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    console.log("Fetch Departments Response:", response.data.data)
    return response.data.data
  } catch (error) {
    console.error("Fetch Departments Error:", error.response?.data || error.message)
    return rejectWithValue(error.response?.data || error.message)
  }
})

// Fetch department by ID
export const fetchDepartmentById = createAsyncThunk(
  "department/fetchDepartmentById",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      // Fix path to match backend: /api/departments/:id
      const response = await axios.get(`${api}/departments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      console.log("Fetch Department By ID Response:", response.data.data)
      return response.data?.data ?? response.data
    } catch (error) {
      console.error("Fetch Department By ID Error:", error.response?.data || error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Create new department
export const addDepartment = createAsyncThunk(
  "department/createDepartment",
  async (departmentData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.post(`${api}/departments`, departmentData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      console.log("Create Department Response:", response.data.data)
      return response.data?.data ?? response.data
    } catch (error) {
      console.error("Create Department Error:", error.response?.data || error.message)
      return rejectWithValue(error.response?.data?.message || "Department creation failed")
    }
  },
)

// Update department
export const editDepartment = createAsyncThunk(
  "department/updateDepartment",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      // Fix path to match backend: /api/departments/:id
      const response = await axios.put(`${api}/departments/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      console.log("Update Department Response:", response.data.data)
      return response.data?.data ?? response.data
    } catch (error) {
      console.error("Update Department Error:", error.response?.data || error.message)
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

// Delete department
export const deleteDepartment = createAsyncThunk("department/deleteDepartment", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    // Fix path to match backend: /api/departments/:id
    await axios.delete(`${api}/departments/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    console.log("Delete Department Success: ID", id)
    return id
  } catch (error) {
    console.error("Delete Department Error:", error.response?.data || error.message)
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

const departmentSlice = createSlice({
  name: "departments",
  initialState: {
    departments: [],
    currentDepartment: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentDepartment: (state) => {
      state.currentDepartment = null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDepartments
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false
        state.departments = action.payload // Store the array of departments
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchDepartmentById
      .addCase(fetchDepartmentById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDepartmentById.fulfilled, (state, action) => {
        state.loading = false
        state.currentDepartment = action.payload
      })
      .addCase(fetchDepartmentById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // createDepartment
      .addCase(addDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addDepartment.fulfilled, (state, action) => {
        state.loading = false
        state.departments = [...state.departments, action.payload]
      })
      .addCase(addDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // updateDepartment
      .addCase(editDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(editDepartment.fulfilled, (state, action) => {
        state.loading = false
        const index = state.departments.findIndex((dept) => dept.department_id === action.payload.department_id)
        if (index !== -1) {
          state.departments[index] = action.payload
        }
        if (state.currentDepartment && state.currentDepartment.department_id === action.payload.department_id) {
          state.currentDepartment = action.payload
        }
      })
      .addCase(editDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // deleteDepartment
      .addCase(deleteDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.loading = false
        state.departments = state.departments.filter((dept) => dept.department_id !== action.payload)
        if (state.currentDepartment && state.currentDepartment.department_id === action.payload) {
          state.currentDepartment = null
        }
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearCurrentDepartment } = departmentSlice.actions
export default departmentSlice.reducer
